import { usePostHog } from '@posthog/react'
import { useEffect } from 'react'

import { VISITOR_COOKIE } from './server/cookies'
import type { PublicProfile } from './server/landingTypes'

/**
 * Wires up the engagement events we care about on top of PostHog autocapture
 * (which `defaults: '2026-01-30'` configures, including pageviews): a stable
 * identity tied to the server-set visitor id, proposal super-properties, and an
 * explicit `conversion` event for primary CTA / contact actions. The
 * authoritative "brief opened" signal is captured server-side; this adds the
 * rich client-side engagement on top.
 *
 * Per the PostHog React guide, the SDK is accessed via `usePostHog()` (never a
 * direct import) and may be uninitialized on the first render, so calls are
 * guarded with optional chaining.
 */
export function useBriefTracking(profile: PublicProfile | null) {
  const posthog = usePostHog()

  useEffect(() => {
    if (!posthog) return

    const properties = {
      proposalId: profile?.proposalId ?? null,
      slug: profile?.slug ?? null,
      externalProposalId: profile?.externalProposalId ?? null,
    }

    // Stable identity across opens / URL changes: reuse the server-set visitor id.
    // Person properties are only $set when a profile is present — on the plain
    // landing page they'd all be null and would wipe the proposal association
    // a returning brief visitor already has.
    const visitorId = readCookie(VISITOR_COOKIE)
    if (visitorId) {
      posthog.identify(visitorId, profile ? properties : undefined)
    }

    posthog.register(properties)

    const onClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return

      const clickable = target.closest('a,button')
      if (!(clickable instanceof HTMLElement)) return

      const placement = findPlacement(clickable)
      const href = clickable instanceof HTMLAnchorElement ? clickable.href : undefined
      if (!isConversion(clickable, placement, href)) return

      const label = clickable.textContent?.trim().replace(/\s+/g, ' ').slice(0, 90) ?? ''
      posthog.capture('conversion', {
        href,
        label,
        placement,
        path: location.pathname,
      })
    }

    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [posthog, profile])
}

/**
 * Scroll reveals: tags <html> with `rv` (so the hidden initial states in CSS
 * only apply once JS is actually running — no-JS visitors and crawlers see the
 * full page) and adds `in` to every [data-reveal] element as it enters the
 * viewport. Skipped entirely for prefers-reduced-motion users.
 */
export function useScrollReveal() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const root = document.documentElement
    root.classList.add('rv')

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          entry.target.classList.add('in')
          observer.unobserve(entry.target)
        }
      },
      // Fire once the element clears the bottom ~10% of the viewport, so the
      // rise-in happens where the eye is, not at the very screen edge.
      { rootMargin: '0px 0px -10% 0px' },
    )
    for (const el of document.querySelectorAll('[data-reveal]')) {
      observer.observe(el)
    }

    return () => {
      observer.disconnect()
      root.classList.remove('rv')
    }
  }, [])
}

/** A conversion is a primary CTA click or any contact/email action. */
function isConversion(element: HTMLElement, placement: string, href?: string) {
  if (href?.startsWith('mailto:')) return true
  if (placement === 'contact') return true
  return element.classList.contains('pri')
}

function findPlacement(element: HTMLElement) {
  if (element.closest('nav')) return 'nav'
  if (element.closest('#hero')) return 'hero'
  if (element.closest('#work')) return 'work'
  if (element.closest('#services')) return 'services'
  if (element.closest('#contact')) return 'contact'
  return 'page'
}

function readCookie(name: string) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}
